import React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import useLogout from "../../../hooks/useLogout";
type Props = {
  inactivityMinutes: number;
};
export default function SessionWatcher({ inactivityMinutes }: Props) {
  const { logout } = useLogout();

  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLogoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogOpenRef = useRef(false);

  const [openDialog, setOpenDialog] = useState(false);
  const warningTime = (inactivityMinutes - 1) * 60 * 1000;
  const logoutTime = inactivityMinutes * 60 * 1000;

  const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

  const resetTimers = () => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    clearTimeout(autoLogoutTimer.current);

    setOpenDialog(false);
    dialogOpenRef.current = false;

    // Show warning dialog
    warningTimer.current = setTimeout(() => {
      setOpenDialog(true);
      dialogOpenRef.current = true;

      // Auto logout after 1 minute if no response
      autoLogoutTimer.current = setTimeout(() => {
        logout("inactivity");
      }, 60000);
    }, warningTime);

    // Final logout timer
    logoutTimer.current = setTimeout(() => {
      logout("inactivity");
    }, logoutTime);
  };

  const handleActivity = () => {
    // Ignore activity when dialog is open
    if (!dialogOpenRef.current) {
      resetTimers();
    }
  };

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity));

    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));

      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
      clearTimeout(autoLogoutTimer.current);
    };
  }, []);

  const staySignedIn = () => {
    clearTimeout(autoLogoutTimer.current);
    resetTimers();
  };

  const logoutNow = () => {
    logout("manual");
  };

  return (
    <Dialog open={openDialog}>
      <DialogTitle
        sx={{
          fontSize: "18px",
        }}
      >
        Session Timeout Warning
      </DialogTitle>

      <DialogContent>
        <Typography>
          You will be logged out due to inactivity in 1 minute. Do you want to stay signed in?
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={logoutNow}>
          Log Out Now
        </Button>

        <Button variant="contained" onClick={staySignedIn}>
          Stay Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
}
